import { FC, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';

import Button from '../../../components/button/index';
import { DividendPayoutType } from '../../../hooks/dividend/dividend.types';
import { DividendHooks } from '../../../hooks/index';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import { getTranslation } from '../../../utils/get-translation.util';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';
import { AddDividendFormData } from './add-dividend.validation';
import { DividendReviewPayoutModal, ReviewPayoutRow } from './dividend-review-payout-modal';
import {
  countImpactedStakeholders,
  getPeriodCovered,
  SummaryHighlights,
  SummaryRow,
  SummarySection,
} from './dividend-summary-parts';

type Props = {
  companyId: string;
  control: Control<AddDividendFormData>;
  currencySymbol: string;
  isEditMode?: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSubmit: () => void;
};

const SUBMIT_LABELS: Record<'add' | 'edit', { idle: string; submitting: string }> = {
  add: {
    idle: getTranslation('ADD_DIVIDEND'),
    submitting: getTranslation('ADD_DIVIDEND_SUBMITTING'),
  },
  edit: {
    idle: getTranslation('SAVE_CHANGES'),
    submitting: getTranslation('SAVE_CHANGES_SUBMITTING'),
  },
};

export const AddDividendStepFive: FC<Props> = ({
  companyId,
  control,
  currencySymbol,
  isEditMode,
  isSubmitting,
  onBack,
  onCancel,
  onSubmit,
}) => {
  const formValues = useWatch({ control });
  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });
  const { holdersByShareClass } = DividendHooks.useDividendShareClassHolders({ companyId });
  const [reviewOpen, setReviewOpen] = useState(false);

  const { type, name, frequency, year, periodIndex, items = [] } = formValues;

  const isShareClass = type === DividendPayoutType.PER_SHARE_CLASS;
  const periodCovered = getPeriodCovered({ frequency, periodIndex, year });

  const formatAmount = (value: number) => `${currencySymbol} ${formatDividendAmount(value)}`;

  const itemPayout = (item: (typeof items)[number]): number => {
    if (isShareClass) {
      const shareClass = shareClasses.find((s) => s.id === item.shareClassId);
      return shareClass?.totalShares && item.amountPerShare
        ? shareClass.totalShares * item.amountPerShare
        : 0;
    }
    return item.totalAmount ?? 0;
  };

  const totalPayout = items.reduce((sum, item) => sum + itemPayout(item), 0);

  const impactedStakeholders = countImpactedStakeholders({ items, shareClasses, type });

  const stakeholderDocs = formValues.stakeholderDocuments ?? [];
  const filesForStakeholder = (stakeholderId?: string): string[] =>
    stakeholderDocs.find((doc) => doc.stakeholderId === stakeholderId)?.filesLinks ?? [];

  const reviewRows: ReviewPayoutRow[] = isShareClass
    ? items.flatMap((item) => {
        const shareClass = shareClasses.find((s) => s.id === item.shareClassId);
        const holders = holdersByShareClass.get(item.shareClassId ?? '') ?? [];
        return holders.map((holder) => ({
          dividendPerShare: item.amountPerShare ?? 0,
          files: filesForStakeholder(holder.stakeholderId),
          shareAmount: holder.shares,
          shareClass: shareClass?.name ?? item.shareClassName ?? '—',
          stakeholder: holder.fullName,
          totalPayout: (holder.shares ?? 0) * (item.amountPerShare ?? 0),
        }));
      })
    : items.map((item) => {
        const holders = holdersByShareClass.get(item.shareClassId ?? '') ?? [];
        const shares =
          holders.find((holder) => holder.stakeholderId === item.stakeholderId)?.shares ?? 0;
        return {
          dividendPerShare: item.amountPerShare ?? 0,
          files: filesForStakeholder(item.stakeholderId),
          shareAmount: shares,
          shareClass: item.shareClassName ?? '—',
          stakeholder: item.stakeholderName ?? '—',
          totalPayout: item.totalAmount ?? shares * (item.amountPerShare ?? 0),
        };
      });

  const reviewSubtitle = [name, periodCovered].filter(Boolean).join(' · ');

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-6 pt-4">
        <SummaryHighlights
          impactedStakeholders={impactedStakeholders}
          onReview={() => setReviewOpen(true)}
          totalPayout={formatAmount(totalPayout)}
        />

        <SummarySection title={getTranslation('GENERAL')}>
          <SummaryRow label={getTranslation('PAYOUT_NAME')} value={name} />
          <SummaryRow label={getTranslation('PERIOD_COVERED')} value={periodCovered} />
          <SummaryRow
            label={getTranslation('DISTRIBUTION')}
            value={getTranslation(isShareClass ? 'PER_SHARE_CLASS' : 'PER_STAKEHOLDER')}
          />
        </SummarySection>

        {items.map((item, index) => {
          const shareClass = shareClasses.find((s) => s.id === item.shareClassId);
          const title =
            (isShareClass ? item.shareClassName : item.stakeholderName) ?? shareClass?.name ?? '—';

          return (
            <SummarySection key={index} strong title={title}>
              {!isShareClass && (
                <SummaryRow
                  label={getTranslation('SHARE_CLASS')}
                  value={item.shareClassName ?? shareClass?.name ?? '—'}
                />
              )}
              <SummaryRow
                label={getTranslation('DIVIDEND_PER_SHARE')}
                value={item.amountPerShare ?? '—'}
              />
              {isShareClass && (
                <SummaryRow
                  label={getTranslation('IMPACTED_STAKEHOLDERS')}
                  value={shareClass?.stakeholders ?? 0}
                />
              )}
              <SummaryRow
                highlight
                label={getTranslation('TOTAL_DIVIDEND_PAYOUT')}
                value={formatAmount(itemPayout(item))}
              />
            </SummarySection>
          );
        })}
      </div>

      <div className="flex w-full shrink-0 items-center justify-between px-6">
        <Button
          className="h-9 w-fit bg-transparent px-3 py-1.5 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          {getTranslation('CANCEL')}
        </Button>
        <div className="flex items-center gap-3">
          <Button
            className="h-9 w-fit rounded border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
            onClick={onBack}
            styleType="NONE"
            type="button"
          >
            {getTranslation('BACK')}
          </Button>
          <Button
            className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white disabled:opacity-50"
            disabled={isSubmitting}
            onClick={onSubmit}
            type="button"
          >
            {SUBMIT_LABELS[isEditMode ? 'edit' : 'add'][isSubmitting ? 'submitting' : 'idle']}
          </Button>
        </div>
      </div>

      <DividendReviewPayoutModal
        companyId={companyId}
        currencySymbol={currencySymbol}
        onClose={() => setReviewOpen(false)}
        open={reviewOpen}
        period={periodCovered}
        rows={reviewRows}
        subtitle={reviewSubtitle}
      />
    </>
  );
};
